import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Device, LocationInfo, WebSocketService, locationTransformer, OutdoorGeofencingMode } from '@ixicare/resources';
import React from 'react';
import { useProfileConfiguration } from '~utils/profile';
import { useSiteConfiguration } from '~containers/ConfigurationForm/ConfigurationForm';

export function useLocation(deviceId?: number, initialData?: LocationInfo, isStatic = false) {
  const { data: lastDeviceLocation } = useQuery({
    queryKey: ['device-location', deviceId],
    queryFn: () => Device.getDeviceLastLocation(deviceId),
    enabled: deviceId !== undefined && !initialData,
  });
  const [data, setData] = React.useState<LocationInfo | undefined>(initialData);

  React.useEffect(() => {
    if (lastDeviceLocation) setData((curr) => (curr ? curr : (lastDeviceLocation?.lastKnown ?? undefined)));
  }, [lastDeviceLocation]);

  // A static device never moves, so there is nothing to stream — skip the socket entirely and
  // keep showing the (last known) static position instead of letting a stray message overwrite it.
  const url =
    !isStatic && deviceId !== undefined ? `/location-streaming/${deviceId}?use_updated_schemas=true` : undefined;

  const socket = React.useMemo(() => (url ? new WebSocketService<LocationInfo>(url, setData) : undefined), [url]);

  React.useEffect(() => {
    socket?.open();
    return () => socket?.forceDisconnect();
  }, [url]);

  return data;
}

const MINUTE = 60 * 1000;

/** Positions for a timeline window. While live, positions streamed since the last fetch are added on top. */
export function useLocationHistory(
  deviceId: number | undefined,
  start: number,
  end: number,
  live: boolean
): { locations: LocationInfo[]; isLoading: boolean } {
  const fromMinute = Math.floor(start / MINUTE);
  const toMinute = Math.ceil(end / MINUTE);
  const { data, isLoading } = useQuery({
    queryKey: ['device-location-history', deviceId, fromMinute, toMinute],
    queryFn: ({ signal }) =>
      Device.getDeviceLocationHistory(deviceId!, new Date(fromMinute * MINUTE), new Date(toMinute * MINUTE), signal),
    enabled: deviceId !== undefined,
    placeholderData: keepPreviousData,
  });

  const latest = useLocation(live ? deviceId : undefined);
  const [streamed, setStreamed] = React.useState<LocationInfo[]>([]);
  React.useEffect(() => {
    if (latest)
      setStreamed((current) =>
        current.some((l) => l.timestamp === latest.timestamp) ? current : [...current, latest]
      );
  }, [latest]);

  const locations = React.useMemo(() => [...(data ?? []), ...streamed], [data, streamed]);
  return { locations, isLoading };
}

/** Whether the site only shows positions during alarms; undefined until its configuration has loaded. */
export function useLocationRestrictedToAlarms(siteUuid?: string | null) {
  const { data, isLoading } = useSiteConfiguration(siteUuid ?? null);
  if (isLoading) return undefined;
  return data?.['restrict-location-to-alarms'] ?? false;
}

export function useGPSGeofence(residentId?: number) {
  const profileConfig = useProfileConfiguration(residentId ?? -1);
  if (profileConfig.data && profileConfig.data.outdoorGeofencingMode === OutdoorGeofencingMode.Geo) {
    return profileConfig.data.outdoorGeofencingGeo;
  }
  return undefined;
}

// Helper for debugging issues, use this instead of `useLocation` and use the steps from the app logs.
export function useLocationPlayback(): LocationInfo {
  const locations: any[] = [
    {
      _discriminator: 'OUTDOOR',
      accuracy: 471,
      coords: {
        srid: 4326,
        x: 77.57716369628906,
        y: 13.008087158203125,
      },
      description: null,
      latitude: 13.008087158203125,
      longitude: 77.57716369628906,
      timestamp: '2024-02-27T07:26:59.363188',
    },
    {
      _discriminator: 'INDOOR',
      accuracy: null,
      coords: {
        _transformations: [
          {
            srid: 990000,
            x: null,
            y: null,
          },
        ],
        x: null,
        y: null,
      },
      description: '0 (new), Office Room',
      floor_number: 0,
      pixels: {
        x: null,
        y: null,
      },
      plane_id: 155,
      plane_uuid: 'eebf29ba-eede-49c6-a515-9ee7b33749bc',
      room_number: 'Office Room',
      site_id: 204,
      site_uuid: 'b9f60c31-d089-459f-9d33-aea7479f4432',
      timestamp: '2024-02-27T07:27:47',
      x: null,
      y: null,
      zone_id: '87db453a-5927-43e7-ac94-e40018f2f049',
    },
  ].map(locationTransformer);
  const [counter, setCounter] = React.useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => setCounter((x) => x + 1), 10e3);
    return () => clearInterval(interval);
  }, []);
  return locations[counter % locations.length];
}
